import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactDataFormKeys,
  confirmEmailField,
  contactDataStepFormValidationSchema,
  emailField,
  errorMessageToFieldNameMap,
  phoneNumberField,
} from './ContactDataStep.config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import {
  useVerifyEmailMutation,
  useVerifyMobileMutation,
} from 'api/validation/validation';
import { RoutePath } from 'router/router.config';
import { useAppDispatch, useAppSelector } from 'store/store.hooks';
import { selectRegistrationState, setValues } from 'store/slices/registration';
import { isTServerErrorMessage } from 'api/api.utils';
import { buildAutocompleteHandlerCreator } from 'components/Form/Form.helpers';
import { usePromotionsQuery } from 'api/signUp/signUp';
import { makeDefaultSelectedPromoCode } from 'pages/PersonalDataStep/PersonalDataStep.helpers';
import { trackTap } from 'utils/functions/analytics';
import { goTryRaw } from 'utils/functions/go-go-try';
import { sayError } from 'utils/functions/say';

export const useContactDataStepForm = () => {
  /*********************************************************
   *                     SERVICE HOOKS                     *
   ********************************************************/
  const { data: promotionsData, isLoading: isPromotionsLoading } =
    usePromotionsQuery();
  const [verifyEmail] = useVerifyEmailMutation();
  const [verifyMobileNumber] = useVerifyMobileMutation();
  /*********************************************************
   *                      STORE HOOKS                      *
   ********************************************************/
  const dispatch = useAppDispatch();
  const { contactDataStep, pdvCode, personalDataStep } = useAppSelector(
    selectRegistrationState,
  );
  /*********************************************************
   *                     UTILITY HOOKS                     *
   ********************************************************/
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  /*********************************************************
   *                     FORM CONFIGS                      *
   ********************************************************/
  const methods = useForm({
    defaultValues: contactDataStep.formValues,
    resolver: zodResolver(contactDataStepFormValidationSchema),
    mode: 'onChange',
  });
  /*********************************************************
   *                   FORM SUBMIT HANDLER                 *
   ********************************************************/
  const handleSubmit = methods.handleSubmit(async function (data) {
    /* -------------------------------------------------------------------------------------------------
     * VERIFY EMAIL FLOW
     * -----------------------------------------------------------------------------------------------*/
    const email = data[emailField]
    if (email != contactDataStep.formValues[emailField]) {
      const [verifyEmailError] = await goTryRaw(
        verifyEmail({
          email: email,
        }).unwrap(),
      );

      if (verifyEmailError) {
        sayError('Verify email error', verifyEmailError);
        return handleVerifyError(verifyEmailError);
      }
    }
    /* -------------------------------------------------------------------------------------------------
     * VERIFY MOBILE FLOW
     * -----------------------------------------------------------------------------------------------*/
    const [verifyMobileNumberError] = await goTryRaw(
      verifyMobileNumber({
        mobilePhoneNumber: data[phoneNumberField],
      }).unwrap(),
    );

    if (verifyMobileNumberError) {
      sayError('Verify mobile error', verifyMobileNumberError);
      return handleVerifyError(verifyMobileNumberError);
    }
    /* -------------------------------------------------------------------------------------------------
     * NEXT STEP FLOW
     * -----------------------------------------------------------------------------------------------*/
    dispatch(setValues({ type: 'SET_CONTACT_DATA_VALUES', formValues: data }));

    //This is needed to set the default promo code from queryParams
    dispatch(
      setValues({
        type: 'SET_PERSONAL_DATA_VALUES',
        formValues: {
          ...personalDataStep.formValues,
          promoCode: makeDefaultSelectedPromoCode(promotionsData, queryParams),
        },
      }),
    );
    navigate(contactDataStep.nextStep);
  });
  /*********************************************************
   *                        HELPERS                        *
   ********************************************************/

  const handleVerifyError = (error: unknown) => {
    if (isTServerErrorMessage(error)) {
      const { errorMessage } = error;
      trackTap({
        event_type: 'registration-error',
        error_field: errorMessageToFieldNameMap[errorMessage],
        error_type: errorMessage,
      });
      return methods.setError(errorMessageToFieldNameMap[errorMessage], {
        type: 'custom',
        message: errorMessage,
      });
    }
    return navigate(RoutePath.Oops);
  };

  const createHandleAutocompleteOnChangeCallback =
    buildAutocompleteHandlerCreator(methods);

  const touchFieldIfAutoFilled = useCallback(
    createHandleAutocompleteOnChangeCallback(),
    [],
  );

  const touchAndTriggerField = (
    fieldName: ContactDataFormKeys,
    fieldValue: string,
  ) => {
    methods.setValue(fieldName, fieldValue, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setTimeout(() => {
      methods.trigger(fieldName);
    }, 0);
  };
  /*********************************************************
   *                        EFFECTS                        *
   ********************************************************/
  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      if (name === emailField) methods.trigger([confirmEmailField]);
    });
    return () => subscription.unsubscribe();
  }, [methods]);
  useEffect(() => {
    if (contactDataStep.isDirty || pdvCode.fromPdv) {
      touchAndTriggerField(emailField, methods.getValues(emailField));
      touchAndTriggerField(
        confirmEmailField,
        methods.getValues(confirmEmailField),
      );
    }
  }, []);
  /*********************************************************
   *                    STATUS VARIABLES                   *
   ********************************************************/
  const isLoading = methods.formState.isSubmitting || isPromotionsLoading;

  return {
    methods,
    contactDataStep,
    isLoading,
    handleSubmit,
    touchFieldIfAutoFilled,
  };
};
